import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { SolIcon } from '@sol/icons';
import { SolButtonComponent } from '../../core/sol-button/sol-button.component';
import { AbstractComponent } from '../../abstract.component';
import {
  ShortcutService,
  SolShortcut,
  SolShortcutAction,
  SolShortcutSubscription,
} from '../../../services/shortcut.service';
import { SolDialog } from '../../../models/core/sol-dialog';
import { SolButtonType } from '../../../models/core/sol-button';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { BrowserTools } from '../../../tools/browser-tools';
import { SolDialogViewState } from './sol-dialog.state';

@Component({
  selector: 'sol-dialog',
  imports: [SolButtonComponent, TranslatePipe],
  templateUrl: './sol-dialog.component.html',
})
export class SolDialogComponent extends AbstractComponent<SolDialogViewState> implements AfterViewInit, OnDestroy {
  // Static refs
  protected readonly SolIcon = SolIcon;
  protected readonly SolButtonType = SolButtonType;

  // Dependencies
  private readonly shortcutService = inject(ShortcutService);

  // Computed
  protected readonly hostClass$ = this.compute(state => state.hostClass);
  protected readonly titleClass$ = this.compute(state => state.titleClass);
  protected readonly chromeless$ = this.compute(state => state.chromeless);
  protected readonly dialog$ = this.compute(state => state.dialog);
  protected readonly leftButtons$ = this.compute(state => state.leftButtons);
  protected readonly rightButtons$ = this.compute(state => state.rightButtons);
  protected readonly loading$ = this.compute(state => state.effectiveLoading);
  protected readonly loadingActionId$ = this.compute(state => state.loadingActionId);

  // Events
  @Output('action')
  private readonly actionEmitter = new EventEmitter<string>();

  // Binding
  private host = inject(ElementRef);

  // Cache
  private shortcutSubscription?: SolShortcutSubscription;

  constructor() {
    super(SolDialogViewState.of());
  }

  ngAfterViewInit(): void {
    this.registerShortcuts();
  }

  ngOnDestroy(): void {
    this.shortcutSubscription?.unsubscribe();
  }

  @HostBinding('class')
  public get hostClass(): string {
    return this.hostClass$();
  }

  @Input()
  public set dialog(dialog: SolDialog | undefined) {
    this.update(state => state.withDialog(dialog));
  }

  @Input()
  public set loading(loading: boolean) {
    this.update(state => state.withLoading(loading));
  }

  protected onAction(actionId: string) {
    if (this.state.effectiveLoading) {
      return;
    }
    BrowserTools.blurAndNext(() => this.emitAction(actionId));
  }

  private emitAction(actionId: string): void {
    if (this.state.effectiveLoading) {
      return;
    }
    this.actionEmitter.emit(actionId);
  }

  private registerShortcuts(): void {
    const buttons = this.state.dialog?.buttons ?? [];
    const shortcutActions: SolShortcutAction[] = buttons
      .filter(button => button.shortcut !== undefined)
      .map(button => ({
        shortcut: button.shortcut!,
        action: () => {
          this.onAction(button.id);
          return true;
        },
      }));
    if (this.state.chromeless) {
      shortcutActions.push({
        shortcut: SolShortcut.of('Escape'),
        action: () => {
          this.onAction('close');
          return true;
        },
      });
    }
    this.shortcutSubscription = this.shortcutService.register('sol-dialog', true, this.host, shortcutActions);
  }
}
